import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShieldCheck, FlaskConical, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="font-display text-3xl">Product not found.</h2>
        <Link to="/shop" className="font-mono-ui text-xs underline mt-4 inline-block">Back to shop</Link>
      </div>
    );

  if (!product) return <div className="p-20 text-center font-mono-ui text-xs">LOADING...</div>;

  const variants = product.variants && product.variants.length > 0
    ? product.variants
    : [{ label: product.size, price: product.price, in_stock: true }];
  const variant = variants[selectedVariant] || variants[0];
  const soldOut = variant?.in_stock === false;

  const addToCart = () => {
    if (soldOut) return;
    addItem(
      {
        id: `${product.id}::${variant.label}`,
        name: `${product.name} — ${variant.label}`,
        price: variant.price,
        image_url: product.image_url,
        sku: product.sku,
      },
      qty,
    );
  };

  return (
    <div data-testid="product-detail-page" className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono-ui text-xs mb-6 hover:text-[#ff1493]">
          <ArrowLeft className="w-4 h-4" /> BACK TO SHOP
        </Link>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="relative">
            <div className="absolute -top-5 -left-5 w-full h-full bg-[#ff1493] border-[4px] border-black" />
            <div className="relative bg-white border-[4px] border-black overflow-hidden">
              <img src={product.image_url} alt={product.name} className="w-full h-auto object-cover" />
            </div>
            <div className="relative grid grid-cols-3 gap-3 mt-4">
              <div className="bg-black text-white border-[3px] border-black p-3">
                <div className="font-mono-ui text-[10px] text-[#00d4ff]">PURITY</div>
                <div className="font-display text-lg mt-1">{product.purity}</div>
              </div>
              <div className="bg-white border-[3px] border-black p-3">
                <div className="font-mono-ui text-[10px] text-black/60">SIZE</div>
                <div className="font-display text-lg mt-1">{variant.label}</div>
              </div>
              <div className="bg-[#00d4ff] border-[3px] border-black p-3">
                <div className="font-mono-ui text-[10px]">STOCK</div>
                <div className="font-display text-lg mt-1">{product.stock}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="font-mono-ui text-xs text-[#ff1493]">{product.category}</div>
            <h1 data-testid="product-detail-name" className="font-display text-5xl md:text-6xl leading-none mt-2">
              {product.name}
            </h1>
            <div className="font-mono-ui text-[11px] text-black/50 mt-2">SKU · {product.sku}</div>
            <p className="mt-6 text-base leading-relaxed text-black/80">{product.description}</p>

            <div className="mt-8 flex items-end gap-5">
              <div>
                <div className="font-mono-ui text-[10px] text-black/50">PRICE</div>
                <div className="font-display text-5xl text-[#ff1493]">£{variant.price.toFixed(2)}</div>
              </div>
              <div className="font-mono-ui text-[11px] pb-2 text-black/60">+ £7.95 UK tracked shipping</div>
            </div>

            <div className="mt-6">
              <label className="font-mono-ui text-[10px] text-black/60 block mb-1">SELECT SIZE / PACK</label>
              <select
                value={selectedVariant}
                onChange={(e) => setSelectedVariant(Number(e.target.value))}
                data-testid="variant-select"
                className="w-full border-[3px] border-black px-3 py-3 font-mono-ui text-sm font-bold bg-white cursor-pointer"
              >
                {variants.map((v, i) => (
                  <option key={i} value={i}>
                    {v.label} — £{v.price.toFixed(2)}{v.in_stock === false ? " · SOLD OUT" : ""}
                  </option>
                ))}
              </select>
              {soldOut && (
                <div className="mt-2 border-[3px] border-black bg-black text-white px-3 py-2 font-mono-ui text-[11px] font-bold">
                  THIS SIZE IS SOLD OUT · PICK ANOTHER
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center gap-4">
              <div className="inline-flex items-center border-[3px] border-black">
                <button
                  data-testid="qty-decrease"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[#ff1493] hover:text-white"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div data-testid="qty-value" className="w-12 text-center font-mono-ui font-bold">{qty}</div>
                <button
                  data-testid="qty-increase"
                  onClick={() => setQty((q) => q + 1)}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[#ff1493] hover:text-white"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                data-testid="detail-add-to-cart"
                onClick={addToCart}
                disabled={soldOut}
                className={`flex-1 px-5 py-3 border-[3px] border-black shadow-brut font-mono-ui text-xs font-bold ${soldOut ? "bg-black/70 text-white cursor-not-allowed" : "bg-[#ff1493] text-white btn-press"}`}
              >
                {soldOut ? "SOLD OUT" : `ADD ${qty} TO CART`}
              </button>
              <button
                data-testid="detail-buy-now"
                onClick={() => {
                  if (soldOut) return;
                  addToCart();
                  navigate("/cart");
                }}
                disabled={soldOut}
                className={`hidden sm:inline-flex px-5 py-3 border-[3px] border-black shadow-brut font-mono-ui text-xs font-bold ${soldOut ? "bg-black/40 text-white cursor-not-allowed" : "bg-black text-white btn-press"}`}
              >
                BUY NOW
              </button>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              <div className="border-[3px] border-black p-3 flex items-center gap-3 bg-white">
                <ShieldCheck className="w-5 h-5 text-[#ff1493]" />
                <div className="font-mono-ui text-[11px]">HPLC verified</div>
              </div>
              <div className="border-[3px] border-black p-3 flex items-center gap-3 bg-white">
                <FlaskConical className="w-5 h-5 text-[#00d4ff]" />
                <div className="font-mono-ui text-[11px]">Lab research use</div>
              </div>
              <div className="border-[3px] border-black p-3 flex items-center gap-3 bg-[#facc15]">
                <TriangleAlert className="w-5 h-5" />
                <div className="font-mono-ui text-[11px]">Not for human use</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
